import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type Theme = 'default' | 'avocado' | 'eceap';

const STORAGE_KEY = 'ceap-theme';

// Everything except 'default', which is the absence of a data-theme
// attribute rather than a value of its own.
const SELECTABLE_THEMES: Theme[] = ['avocado', 'eceap'];

interface ThemeContextValue {
  // The theme actually showing — 'default' while themes are locked.
  theme: Theme;
  setTheme: (theme: Theme) => void;
  // False when the Super Admin has switched themes off for the signed-in
  // Admin. Set from inside the auth tree (see ThemePermissionSync in
  // App.tsx), since this provider sits outside it.
  themesAllowed: boolean;
  setThemesAllowed: (allowed: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readStoredTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  // Checked against the list rather than one hardcoded value, so adding
  // a theme doesn't silently leave it unrestorable after a reload.
  return SELECTABLE_THEMES.find((theme) => theme === stored) ?? 'default';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [chosenTheme, setChosenTheme] = useState<Theme>(readStoredTheme);
  const [themesAllowed, setThemesAllowed] = useState(true);
  // A lock overrides what's shown without touching what's stored: the
  // device may be shared, and the next person to sign in on it should
  // get their own choice back.
  const theme: Theme = themesAllowed ? chosenTheme : 'default';

  useEffect(() => {
    if (theme === 'default') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
    }
  }, [theme]);

  const setTheme = (next: Theme) => {
    if (!themesAllowed) return;
    localStorage.setItem(STORAGE_KEY, next);
    setChosenTheme(next);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themesAllowed, setThemesAllowed }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
